import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wind, Sun, Zap, TrendingUp, Globe, Shield, ArrowRight, Battery, BarChart3, Users, CheckCircle2, ChevronRight } from "lucide-react";
import heroBg from "../assets/hero-bg.jpg";
import windImg from "../assets/wind-energy.jpg";
import solarImg from "../assets/solar-energy.jpg";
import globalNetwork from "../assets/global-network.jpg";
import controlRoom from "../assets/control-room.jpg";
import AnimatedCounter from "../components/AnimatedCounter";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import EnergyParticles from "../components/EnergyParticles";
import EnergyFlowSVG from "../components/EnergyFlowSVG";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const Home = () => {
  return (
    <div>
      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBg} alt="Renewable energy" className="w-full h-full object-cover scale-105" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <EnergyParticles />

        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: "easeOut" }}>
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 font-display text-xs tracking-[0.35em] uppercase text-primary mb-8 px-5 py-2 rounded-full border border-primary/20 bg-primary/5 backdrop-blur-sm"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse-glow" />
              Powering the Future
            </motion.span>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-8 max-w-5xl mx-auto tracking-tight">
              Invest in{" "}
              <span className="energy-gradient-text">Renewable Energy</span>{" "}
              Infrastructure
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed font-light">
              Participate in wind and solar projects designed to generate sustainable energy and shared revenue opportunities for global partners.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link
                to="/packages"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all glow-box hover:glow-box-hover text-sm"
              >
                Explore Energy Projects <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold border border-border text-foreground hover:border-primary/40 hover:bg-primary/5 transition-all text-sm backdrop-blur-sm"
              >
                Become a Partner <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Mini stats bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="flex flex-wrap justify-center gap-8 text-sm"
            >
              {[
                { label: "Active Partners", value: "32,000+" },
                { label: "Energy Generated", value: "850 MW" },
                { label: "Countries", value: "45+" },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-primary" />
                  <span className="text-muted-foreground">{s.label}:</span>
                  <span className="font-semibold text-foreground">{s.value}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Animated bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <EnergyFlowSVG className="h-24 opacity-40" />
        </div>
      </section>

      {/* ===== LIVE COUNTERS ===== */}
      <Section>
        <SectionHeading
          eyebrow="Live Dashboard"
          title="Real-Time Energy Performance"
          description="Track our infrastructure output in real-time. These figures update based on actual energy generation data."
        />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <AnimatedCounter end={850} suffix=" MW" label="Installed Capacity" />
          <AnimatedCounter end={12400} label="Active Energy Units" />
          <AnimatedCounter end={4200} suffix=" MWh" label="Generated Today" />
          <AnimatedCounter end={32000} label="Global Partners" />
          <AnimatedCounter end={18} suffix="M+" label="Revenue Distributed" />
        </div>
      </Section>

      {/* ===== WHAT WE DO ===== */}
      <Section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={controlRoom} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-background/90" />
        </div>
        <div className="relative z-10">
          <SectionHeading
            eyebrow="Our Platform"
            title="Infrastructure That Powers Nations"
            description="AeroVolt connects global investors with operational renewable energy assets producing real electricity for national power grids."
          />
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Wind, title: "Wind Farm Operations", desc: "We design, build, and operate onshore wind farms using next-generation turbine technology. Each facility is positioned in high-wind corridors verified by 3+ years of meteorological data, ensuring consistent energy output across all seasons.", stat: "14 Active Farms" },
              { icon: Sun, title: "Solar Array Networks", desc: "Our photovoltaic installations leverage Tier-1 panels with 22%+ efficiency ratings. Smart tracking systems follow the sun's arc throughout the day, maximizing energy capture by up to 35% compared to fixed installations.", stat: "22 Solar Sites" },
              { icon: BarChart3, title: "Grid Distribution", desc: "All generated energy is fed directly into national power grids through certified interconnection agreements. Revenue is generated through long-term Power Purchase Agreements (PPAs) with utility companies and government bodies.", stat: "£4.2M Monthly" },
            ].map((item, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.15 }}
                className="group p-8 rounded-2xl glass-card glass-card-hover transition-all duration-500"
              >
                <div className="w-14 h-14 rounded-xl energy-gradient flex items-center justify-center mb-5 group-hover:shadow-[0_0_25px_hsl(155_80%_50%/0.3)] transition-shadow">
                  <item.icon className="w-7 h-7 text-primary-foreground" />
                </div>
                <h3 className="font-display text-lg font-semibold mb-3">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">{item.desc}</p>
                <div className="flex items-center gap-2 text-primary text-sm font-medium">
                  <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  {item.stat}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ===== WHY RENEWABLE ===== */}
      <Section>
        <SectionHeading
          eyebrow="Market Opportunity"
          title="Why Renewable Energy?"
          description="The global energy transition is the largest infrastructure investment opportunity in history."
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            {[
              { icon: TrendingUp, title: "Exponential Growth", desc: "Global renewable capacity is projected to reach 7,300 GW by 2028, tripling from 2022 levels. Investment in clean energy exceeded $500 billion in 2024 alone." },
              { icon: Shield, title: "Government-Backed Revenue", desc: "Feed-in tariffs and Power Purchase Agreements guarantee fixed energy prices for 15-25 years, providing stable, predictable revenue streams from grid-connected assets." },
              { icon: Battery, title: "30+ Year Asset Life", desc: "Modern wind turbines and solar panels are engineered for 25-30+ years of operational life with minimal degradation, providing long-term revenue-generating infrastructure." },
              { icon: Globe, title: "Global Market Expansion", desc: "The renewable energy market is projected to reach $2.15 trillion by 2030, with emerging markets in Asia, Africa, and Latin America driving unprecedented demand." },
            ].map((item, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="flex gap-4 group">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold mb-1">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="relative">
            <img src={globalNetwork} alt="Global energy network" className="rounded-2xl w-full glow-box" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent rounded-2xl" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: "$2.15T", l: "Market by 2030" },
                  { v: "7,300 GW", l: "Capacity by 2028" },
                  { v: "45+", l: "Countries Active" },
                  { v: "29%", l: "Annual Growth" },
                ].map((s, i) => (
                  <div key={i} className="p-3 rounded-lg glass-card text-center">
                    <div className="font-display text-sm font-bold text-primary">{s.v}</div>
                    <div className="text-[10px] text-muted-foreground">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ===== WIND & SOLAR PREVIEW ===== */}
      <Section className="bg-secondary/20">
        <SectionHeading eyebrow="Our Projects" title="Wind & Solar Infrastructure" />
        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              img: windImg,
              icon: Wind,
              title: "Wind Energy Division",
              desc: "Our wind division operates 14 onshore farms across the UK and Northern Europe, utilizing Vestas V150-6.0 MW turbines. Combined rated capacity exceeds 520 MW with a 42% average capacity factor — well above the industry average of 34%.",
              stats: ["520 MW Capacity", "14 Active Farms", "42% Capacity Factor"],
              link: "/wind-energy",
            },
            {
              img: solarImg,
              icon: Sun,
              title: "Solar Energy Division",
              desc: "Our solar division manages 22 utility-scale installations across Southern Europe and North Africa, totaling 330 MW of peak capacity. Each site uses bifacial panels with single-axis tracking for maximum energy yield throughout daylight hours.",
              stats: ["330 MW Capacity", "22 Solar Sites", "22%+ Panel Efficiency"],
              link: "/solar-energy",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              {...fadeUp}
              transition={{ delay: i * 0.2 }}
              className="group rounded-2xl overflow-hidden glass-card glass-card-hover transition-all duration-500"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={item.img}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                <div className="absolute top-4 left-4 w-10 h-10 rounded-lg energy-gradient flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-primary-foreground" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-5">{item.desc}</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {item.stats.map((s, j) => (
                    <span key={j} className="px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                      {s}
                    </span>
                  ))}
                </div>
                <Link to={item.link} className="inline-flex items-center gap-2 text-primary text-sm font-semibold group-hover:gap-3 transition-all">
                  Explore Division <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ===== HOW IT WORKS PREVIEW ===== */}
      <Section>
        <SectionHeading
          eyebrow="Simple Process"
          title="How Partner Participation Works"
          description="A transparent four-step process from project development to partner revenue distribution."
        />
        <div className="grid md:grid-cols-4 gap-6 relative">
          {/* Connection line */}
          <div className="absolute top-12 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-primary/0 via-primary/30 to-primary/0 hidden md:block" />
          {[
            { step: "01", icon: Wind, title: "Project Development", desc: "We identify optimal sites, secure permits, and build wind and solar infrastructure." },
            { step: "02", icon: Zap, title: "Energy Generation", desc: "Commissioned assets generate clean electricity fed into national grids 24/7." },
            { step: "03", icon: BarChart3, title: "Revenue Collection", desc: "Energy sales through PPAs and government incentives create predictable revenue." },
            { step: "04", icon: Users, title: "Partner Distribution", desc: "Performance-based distributions allocated via transparent real-time dashboards." },
          ].map((step, i) => (
            <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.15 }} className="text-center relative">
              <div className="relative inline-flex mb-4">
                <div className="w-20 h-20 rounded-2xl energy-gradient flex items-center justify-center animate-glow-pulse">
                  <step.icon className="w-9 h-9 text-primary-foreground" />
                </div>
                <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-background border-2 border-primary flex items-center justify-center font-display text-xs font-bold text-primary">
                  {step.step}
                </span>
              </div>
              <h3 className="font-display text-sm font-semibold mb-2">{step.title}</h3>
              <p className="text-muted-foreground text-xs leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/how-it-works" className="inline-flex items-center gap-2 text-primary text-sm font-semibold hover:gap-3 transition-all">
            Learn More About Our Process <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Section>

      {/* ===== TESTIMONIALS ===== */}
      <Section className="bg-secondary/20">
        <SectionHeading eyebrow="Partner Stories" title="Trusted by Global Partners" />
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { name: "James Whitfield", role: "Energy Investor, London", quote: "AeroVolt's transparency is unmatched. Real-time dashboards show exactly where energy revenue comes from. I've been a partner for 18 months and the consistency is remarkable." },
            { name: "Aisha Rahman", role: "Sustainability Consultant, Dubai", quote: "What drew me to AeroVolt was the tangible infrastructure — real turbines, real solar farms, real energy going into real grids. It's not speculative, it's operational." },
            { name: "Marcus Chen", role: "Renewable Fund Manager, Singapore", quote: "The combination of government-backed PPAs and proven turbine technology gives me confidence. AeroVolt's team has deep expertise in energy infrastructure development." },
          ].map((t, i) => (
            <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.15 }} className="p-6 rounded-2xl glass-card glass-card-hover transition-all duration-500">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, j) => (
                  <span key={j} className="text-primary text-sm">★</span>
                ))}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5 italic">"{t.quote}"</p>
              <div>
                <div className="font-display text-sm font-semibold">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ===== CTA ===== */}
      <Section className="relative overflow-hidden">
        <EnergyParticles />
        <div className="relative z-10 text-center max-w-2xl mx-auto">
          <motion.div {...fadeUp}>
            <Zap className="w-14 h-14 text-primary mx-auto mb-6 animate-pulse-glow" />
            <h2 className="text-3xl md:text-5xl font-bold mb-4">Ready to Power the Future?</h2>
            <p className="text-muted-foreground mb-10 text-lg">
              Join 32,000+ global partners participating in sustainable energy infrastructure generating real revenue from real power.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/packages"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all glow-box hover:glow-box-hover text-sm"
              >
                View Energy Packages <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold border border-border text-foreground hover:border-primary/40 transition-all text-sm"
              >
                Contact Our Team
              </Link>
            </div>
          </motion.div>
        </div>
      </Section>
    </div>
  );
};

export default Home;
